import React, { useState, useEffect } from 'react'
import { Field, SubmissionError } from 'redux-form'
import styled from 'styled-components'

import Theme from 'styles/theme'

import { CardElement, useElements } from '@stripe/react-stripe-js'

import {
  InputGroup,
  InlineInput,
  InlineInputGroup,
  renderTextInput,
  renderError,
  ErrorLabel,
} from '../../../components/form'

type CardNumberProps = {
  $invalid?: boolean
  $rounded?: boolean
}

const CustomCardNumberElement = styled(CardElement)<CardNumberProps>`
  height: 60px;
  padding: 18px;
  margin: 8px 0 8px;
  border: 1px solid #d5d5d5;

  ${({ $rounded = true }) =>
    $rounded &&
    `
    border-radius: 6px;
  `}

  &.StripeElement--invalid {
    border-color: ${({ theme }) => theme?.['-ds-error-300']?.value};
  }

  &.StripeElement--focus {
    border-color: ${({ theme }) => theme.brand.jungle};
  }

  ${({ $invalid, theme }) =>
    $invalid &&
    `
    border-color: ${theme?.['-ds-error-300']?.value};
  `}
`

const CARD_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      color: '#333',
      fontSize: '16px',
      fontWeight: '300',
      fontFamily: Theme.fonts.default,
      letterSpacing: '0.5px',
    },
    invalid: {
      iconColor: Theme?.['-ds-error-300']?.value,
      color: Theme?.['-ds-error-300']?.value,
    },
  },
}

const validCardName = (value: string) =>
  !value ? 'Please add the name on the credit card' : undefined

type NewCreditCardFieldsProps = {
  submitFailed: boolean
  error?: SubmissionError
}

const NewCreditCardFields = ({
  submitFailed,
  error: submitError,
}: NewCreditCardFieldsProps) => {
  const [stripeElementsError, setStripeElementsError] = useState(
    'Please add card number, expiry date, and CVC'
  )
  const elements = useElements()

  useEffect(() => {
    // useElements() will return null until stripe has finished loading.
    // We load stripe asynchronously with loadStripe(), which returns a promise that
    // may not have yet resolved at the time this hook is first executed
    if (elements == null) return

    const cardElement = elements.getElement(CardElement)
    cardElement.on('change', ({ error }) => {
      setStripeElementsError(error ? error.message : '')
    })
  }, [elements])

  const generalFormError = submitError?.errors?._error

  const cardInputInvalid =
    generalFormError != null || (submitFailed && stripeElementsError != null)

  return (
    <div>
      <InputGroup style={{ marginBottom: 0 }}>
        <InlineInputGroup>
          <InlineInput>
            <CustomCardNumberElement
              options={CARD_OPTIONS}
              $invalid={cardInputInvalid}
            />
          </InlineInput>
        </InlineInputGroup>
        {submitFailed && stripeElementsError && generalFormError == null && (
          <ErrorLabel>{stripeElementsError}</ErrorLabel>
        )}
      </InputGroup>

      <InputGroup style={{ marginBottom: 16 }}>
        <InlineInputGroup>
          <InlineInput>
            <Field
              name="payment_method[new_card_name]"
              placeholder="Cardholder name"
              component={renderTextInput}
              validate={[validCardName]}
            />
            {!generalFormError && (
              <Field
                name="payment_method[new_card_name]"
                component={renderError}
                validate={[validCardName]}
              />
            )}
          </InlineInput>
        </InlineInputGroup>

        {generalFormError && <ErrorLabel>{generalFormError}</ErrorLabel>}
      </InputGroup>
    </div>
  )
}

export default NewCreditCardFields
